/**
 * Where the playhead sits, as pure arithmetic.
 *
 * Kept out of the component so it can be tested without a browser: the bar is
 * moved from an animation frame, and an animation frame never runs in a hidden
 * tab, so "look at it" is not a test that can be relied on.
 */

export interface Sounding { start: number; dur: number; i: number }
export interface Slot { x: number; sys: number }
export interface System { y: number; h: number; xEnd: number }

export interface HeadPos { x: number; y: number; h: number; sys: number }

/**
 * Interpolate between the note that has just sounded and the one coming next.
 *
 * Two rules earn their place here:
 *  · the bar sweeps between noteheads rather than hopping from one to the next,
 *    so the eye is led rather than jerked;
 *  · when the next note is on the FOLLOWING system, the bar runs to the end of
 *    the current one and stops. It must never travel diagonally down the page,
 *    and it must never slide backwards across a line to catch up — which is
 *    what a CSS transition on `left` used to make it do.
 */
export function playheadPosition(
  q: number | null,
  sounding: Sounding[],
  pos: Map<number, Slot>,
  systems: System[],
): HeadPos | null {
  if (q == null || !sounding.length || !pos.size || !systems.length) return null;

  // Binary search: this runs on every animation frame.
  let lo = 0, hi = sounding.length - 1, ci = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (sounding[mid].start <= q + 1e-6) { ci = mid; lo = mid + 1; } else hi = mid - 1;
  }
  const cur = sounding[ci];
  const next = sounding[ci + 1] ?? null;
  const a = pos.get(cur.i);
  if (!a) return null;
  const sys = systems[a.sys];
  if (!sys) return null;

  const span = next ? next.start - cur.start : cur.dur;
  const t = span > 0 ? Math.min(1, Math.max(0, (q - cur.start) / span)) : 0;
  const bPos = next ? pos.get(next.i) : undefined;
  const sameSystem = bPos && bPos.sys === a.sys;
  const toX = sameSystem ? bPos!.x : sys.xEnd;
  return { x: a.x + (toX - a.x) * t, y: sys.y - 8, h: sys.h, sys: a.sys };
}
